import React, { useState, useEffect } from 'react';
import {
  FiSettings, FiSave, FiServer,
  FiBell, FiShield, FiDollarSign
} from 'react-icons/fi';
import { api } from '../../services/api';

function AdminSystemConfig() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getSystemSettings();
      if (response?.success && response.data) {
        setConfig(response.data);
      } else {
        throw new Error(response?.message || 'Chargement impossible');
      }
    } catch (err) {
      setError(err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);
      const response = await api.updateSystemSettings(config);
      if (response?.success) {
        setConfig(response.data);
        alert('Configuration enregistrée avec succès');
      } else {
        throw new Error(response?.message || 'Erreur lors de la sauvegarde');
      }
    } catch (err) {
      setError(err.message || 'Erreur lors de la sauvegarde');
      alert(err.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const updateConfig = (category, key, value) => {
    setConfig((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [key]: value,
      },
    }));
  };

  if (loading || !config) {
    return (
      <div className="p-12 text-center text-gray-400">
        {error ? (
          <>
            <p className="text-red-400 mb-4">{error}</p>
            <button
              type="button"
              onClick={loadConfig}
              className="px-4 py-2 bg-yellow-600 text-gray-900 rounded-lg font-semibold"
            >
              Réessayer
            </button>
          </>
        ) : (
          'Chargement de la configuration...'
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-yellow-400 mb-2">Configuration système</h1>
          <p className="text-gray-400">
            Paramètres enregistrés en base — appliqués à l&apos;app et au site
          </p>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="mt-4 sm:mt-0 px-4 py-2 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-all flex items-center disabled:opacity-50"
        >
          <FiSave className="w-5 h-5 mr-2" />
          {saving ? 'Sauvegarde...' : 'Enregistrer'}
        </button>
      </div>

      {error && (
        <p className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded-lg p-3">
          {error}
        </p>
      )}

      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <FiServer className="w-6 h-6 text-yellow-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Paramètres de l&apos;application</h2>
        </div>
        <div className="space-y-4">
          <ToggleRow
            title="Mode maintenance"
            subtitle="Bloque l'app pour les utilisateurs (les admins peuvent encore se connecter)"
            checked={config.app.maintenance}
            onChange={(v) => updateConfig('app', 'maintenance', v)}
          />
          <ToggleRow
            title="Inscriptions ouvertes"
            subtitle="Autoriser les nouvelles inscriptions client et déménageur"
            checked={config.app.registrationEnabled}
            onChange={(v) => updateConfig('app', 'registrationEnabled', v)}
          />
          <ToggleRow
            title="Vérification email requise"
            subtitle="Les nouveaux comptes email doivent être vérifiés avant connexion"
            checked={config.app.emailVerificationRequired}
            onChange={(v) => updateConfig('app', 'emailVerificationRequired', v)}
          />
          <ToggleRow
            title="Vérification téléphone requise"
            subtitle="Code SMS obligatoire à l'inscription client et déménageur"
            checked={config.app.phoneVerificationRequired !== false}
            onChange={(v) => updateConfig('app', 'phoneVerificationRequired', v)}
          />
        </div>
      </div>

      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <FiDollarSign className="w-6 h-6 text-yellow-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Paramètres de paiement</h2>
        </div>
        <div className="space-y-4">
          <ToggleRow
            title="Stripe activé"
            subtitle="Active ou désactive les paiements Stripe"
            checked={config.payments.stripeEnabled}
            onChange={(v) => updateConfig('payments', 'stripeEnabled', v)}
          />
          <div className="p-4 bg-gray-900/50 rounded-lg">
            <label className="block text-white font-semibold mb-2">
              Paiement minimum ({config.payments.currency || 'TND'})
            </label>
            <input
              type="number"
              min="0"
              step="0.01"
              value={config.payments.minPayment}
              onChange={(e) =>
                updateConfig('payments', 'minPayment', parseFloat(e.target.value) || 0)
              }
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            />
          </div>
        </div>
      </div>

      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <FiBell className="w-6 h-6 text-yellow-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Paramètres de notification</h2>
        </div>
        <div className="space-y-4">
          <ToggleRow
            title="Notifications email"
            checked={config.notifications.emailEnabled}
            onChange={(v) => updateConfig('notifications', 'emailEnabled', v)}
          />
          <ToggleRow
            title="Notifications SMS"
            checked={config.notifications.smsEnabled}
            onChange={(v) => updateConfig('notifications', 'smsEnabled', v)}
          />
          <ToggleRow
            title="Notifications push"
            checked={config.notifications.pushEnabled}
            onChange={(v) => updateConfig('notifications', 'pushEnabled', v)}
          />
        </div>
      </div>

      <div className="bg-gray-800 border border-yellow-600/30 rounded-lg p-6">
        <div className="flex items-center mb-6">
          <FiShield className="w-6 h-6 text-yellow-400 mr-2" />
          <h2 className="text-xl font-bold text-white">Paramètres de sécurité</h2>
        </div>
        <div className="space-y-4">
          <ToggleRow
            title="Authentification à deux facteurs requise"
            subtitle="Enregistré pour une utilisation future"
            checked={config.security.twoFactorRequired}
            onChange={(v) => updateConfig('security', 'twoFactorRequired', v)}
          />
          <div className="p-4 bg-gray-900/50 rounded-lg">
            <label className="block text-white font-semibold mb-2">
              Timeout de session (secondes)
            </label>
            <input
              type="number"
              min="300"
              value={config.security.sessionTimeout}
              onChange={(e) =>
                updateConfig('security', 'sessionTimeout', parseInt(e.target.value, 10) || 3600)
              }
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            />
          </div>
          <div className="p-4 bg-gray-900/50 rounded-lg">
            <label className="block text-white font-semibold mb-2">
              Nombre maximum de tentatives de connexion
            </label>
            <input
              type="number"
              min="1"
              value={config.security.maxLoginAttempts}
              onChange={(e) =>
                updateConfig('security', 'maxLoginAttempts', parseInt(e.target.value, 10) || 5)
              }
              className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white focus:outline-none focus:border-yellow-500"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ToggleRow({ title, subtitle, checked, onChange }) {
  return (
    <div className="flex items-center justify-between p-4 bg-gray-900/50 rounded-lg">
      <div>
        <p className="text-white font-semibold">{title}</p>
        {subtitle && <p className="text-gray-400 text-sm">{subtitle}</p>}
      </div>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          checked={!!checked}
          onChange={(e) => onChange(e.target.checked)}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-yellow-500/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-600" />
      </label>
    </div>
  );
}

export default AdminSystemConfig;
